/**
 * Wipe ALL survey offline state on logout / user-switch (shared field-tablet
 * security). The durable outbox (main-field-mutations), the pending-photo blob
 * store (main-field-photos), and the localStorage note backups otherwise survive
 * a sign-out — so the NEXT rep on the same tablet would drain the previous rep's
 * queued survey writes under their own identity, and stale note backups could
 * bleed across users.
 *
 * This is a PURE UNCONDITIONAL WIPE with NO pre-drain. A pre-drain is unsafe on a
 * user-switch: `onAuthStateChanged` has already set `auth.currentUser` to the
 * INCOMING user before `clearOfflineCache` runs, so a `sync()` here would drain
 * the OUTGOING user's queued mutations under the INCOMING user's token —
 * committing/exposing Rep A's data as Rep B. So the outgoing user's not-yet-
 * synced work is DISCARDED on sign-out/switch. Device security on a shared tablet
 * wins over preserving one user's offline drafts across an explicit logout — the
 * same call already terminates and clears the Firestore IndexedDB cache.
 */
import { surveyMutationQueue, surveyPhotoStore } from './offlineEngine';
import { clearAllSurveyLocalBackups } from './notesBackup';

/**
 * UNCONDITIONALLY clear the outbox, the blob store, and the local backups. Never
 * throws — mirrors clearOfflineCache's tolerance so it can never block sign-out.
 */
export async function clearSurveyOfflineData(): Promise<void> {
  // Wipe every survey-offline surface, each independently so one failure doesn't
  // skip the others. No pre-drain (see header — would run under the wrong
  // identity on a user-switch).
  try {
    await surveyMutationQueue.clear();
  } catch {
    // ignore — best-effort
  }
  try {
    await surveyPhotoStore.clear();
  } catch {
    // ignore — best-effort
  }
  clearAllSurveyLocalBackups();
}
